import { type Direction, isCardinal, cycleDirection, reverseDirection } from "../engine/direction";
import type { Effect } from "../engine/effects/effect";
import { FadeIn } from "../engine/effects/fadeIn";
import { FadeOut } from "../engine/effects/fadeOut";
import { MovementEffect } from "../engine/effects/movementEffect";
import {
	type PointsToDirection,
	isPointsToDirection,
	type LookAtHorizontalDirection,
	isLookAtHorizontalDirection,
	isSomethingWithState,
} from "../engine/interfaces";
import { randomInt } from "../engine/random";
import type { PhysicalThing } from "../engine/physicalThing";
import { Player } from "../engine/player";
import { MultiTileThingCore } from "../engine/things/multitile/multiTileThingCore";
import { MultiTileThingPart } from "../engine/things/multitile/multiTileThingPart";
import { Text } from "../engine/things/text";
import type { AnimationConfig } from "./tilesetConfig/animationConfig";
import type { TilesetConfig } from "./tilesetConfig/tilesetConfig";
import type { TilesetConfigEntry } from "./tilesetConfig/tilesetConfigEntry";
import { ImageTransformation } from "./sprites/imageTransformation";
import { Sprite } from "./sprites/sprite";
import { SpriteAnimation } from "./sprites/spriteAnimation";
import { getImage } from "./tileImageLoader";
import { playerSpriteComposer } from "./playerSpriteComposer";
import type { PlayerVisualStateProvider } from "./playerVisualState";

export class AnimationLoader {
	private config: TilesetConfig;
	private cellWidth: number;
	private cellHeight: number;
	private playerVisualStateProvider: PlayerVisualStateProvider | null = null;

	constructor(config: TilesetConfig, cellWidth: number, cellHeight: number) {
		this.config = config;
		this.cellWidth = cellWidth;
		this.cellHeight = cellHeight;
	}

	setCellSize(cellWidth: number, cellHeight: number): void {
		this.cellWidth = cellWidth;
		this.cellHeight = cellHeight;
	}

	setPlayerVisualStateProvider(provider: PlayerVisualStateProvider | null): void {
		this.playerVisualStateProvider = provider;
	}

	getPlayerVisualStateProvider(): PlayerVisualStateProvider | null {
		return this.playerVisualStateProvider;
	}

	private async resolve(
		entry: TilesetConfigEntry,
		opts: {
			filter?: (anims: AnimationConfig[], entry: TilesetConfigEntry) => AnimationConfig[];
			pickVariant: (count: number) => number;
			buildTransform?: (chosen: AnimationConfig, transformation: ImageTransformation) => void;
		},
	): Promise<SpriteAnimation | null> {
		const filter = opts.filter ?? ((anims) => anims);
		const animations = filter(entry.animations, entry);
		if (animations.length === 0) return null;

		const animConfig = animations[opts.pickVariant(animations.length)];

		const transformation = new ImageTransformation();
		opts.buildTransform?.(animConfig, transformation);

		return this.loadAnimationFromConfig(animConfig, transformation);
	}

	async getAnimation(thing: PhysicalThing): Promise<SpriteAnimation | null> {
		// Composed here (not via tileset entry) so the MovementEffect path shares the player sprite.
		if (thing instanceof Player && this.playerVisualStateProvider) {
			return playerSpriteComposer.getAnimation(
				this.playerVisualStateProvider.getState(),
				thing.getLookingAtHorizontalDirection(),
				this.cellWidth,
				this.cellHeight,
			);
		}

		// Multi-tile: core doesn't render — parts handle rendering
		if (thing instanceof MultiTileThingCore) return null;

		// Text entities are rendered by FloatingTextRenderer, not the sprite pipeline.
		if (thing instanceof Text) return null;

		// MultiTileThingPart: only the render anchor (screen bottom-left) renders the core's sprite
		if (thing instanceof MultiTileThingPart) {
			const anchorXY = thing.controller.getRenderAnchorXY();
			if (!thing.xy.equals(anchorXY)) return null;
			thing = thing.controller.getThing();
		}

		const key = thing.getAnimationKey();
		let entry = this.config.getEntry(key);
		if (!entry) {
			console.warn(`AnimationLoader: no tileset config for key "${key}", falling back to placeholder`);
			entry = this.config.getEntry("placeholder");
			if (!entry) return null;
		}

		const resolvedThing = thing;
		const resolvedEntry = entry;
		return this.resolve(resolvedEntry, {
			filter: (_, e) => this.getViableAnimations(resolvedThing, e),
			pickVariant: (n) => resolvedThing.visualId % n,
			buildTransform: (animConfig, transformation) => {
				if (!resolvedEntry.allowTransform) return;
				if (isPointsToDirection(resolvedThing)) {
					this.transformPointsToDirection(resolvedThing, animConfig, transformation);
				}
				if (isLookAtHorizontalDirection(resolvedThing)) {
					this.transformLookAtHorizontalDirection(resolvedThing, animConfig, transformation);
				}
			},
		});
	}

	async getAnimationForEffect(effect: Effect): Promise<SpriteAnimation | null> {
		if (effect instanceof MovementEffect) {
			return this.getAnimation(effect.thing);
		}

		const key = effect.getAnimationKey();
		const entry = this.config.getEntry(key);
		if (!entry) {
			console.warn(`AnimationLoader: no tileset config for effect key "${key}"`);
			return null;
		}

		const spriteAnimation = await this.resolve(entry, {
			filter: (anims, e) =>
				e.allowTransform && isPointsToDirection(effect)
					? this.filterPointsToDirection(effect, anims, e)
					: anims,
			pickVariant: (n) => randomInt(0, n - 1),
			buildTransform: (animConfig, transformation) => {
				if (entry.allowTransform && isPointsToDirection(effect)) {
					this.transformPointsToDirection(effect, animConfig, transformation);
				}
			},
		});

		if (spriteAnimation === null) return null;

		if (effect instanceof FadeIn) {
			for (const frame of spriteAnimation.frames) {
				frame.interval *= effect.getTimer();
			}
		} else if (effect instanceof FadeOut) {
			if (spriteAnimation.frames.length >= 2) {
				spriteAnimation.frames[0].interval *= effect.getBlankTimer();
				spriteAnimation.frames[1].interval *= effect.getBlackTimer();
			}
		}

		return spriteAnimation;
	}

	async getAnimationByKey(key: string, visualId: number): Promise<SpriteAnimation | null> {
		let entry = this.config.getEntry(key);
		if (!entry) {
			entry = this.config.getEntry("placeholder");
			if (!entry) return null;
		}
		return this.resolve(entry, { pickVariant: (n) => visualId % n });
	}

	private getViableAnimations(thing: unknown, entry: TilesetConfigEntry): AnimationConfig[] {
		let animations = entry.animations;

		if (isPointsToDirection(thing)) {
			animations = this.filterPointsToDirection(thing, animations, entry);
		}

		if (isLookAtHorizontalDirection(thing) && !isPointsToDirection(thing)) {
			animations = this.filterLookAtHorizontalDirection(thing, animations, entry);
		}

		if (isSomethingWithState(thing)) {
			animations = filterByState(thing.getState(), animations);
		}

		return animations;
	}

	private filterPointsToDirection(
		thing: PointsToDirection,
		animations: AnimationConfig[],
		entry: TilesetConfigEntry,
	): AnimationConfig[] {
		const desired = thing.getDirection();
		if (desired === null) return animations;

		// Plan A: animations that directly point to the desired direction
		const planA = animations.filter((a) => a.pointsTo.includes(desired));
		if (planA.length > 0) return planA;

		// Plan B: animations that can be transformed (same cardinality)
		if (entry.allowTransform) {
			const planB = animations.filter((a) => a.pointsTo.some((d) => isCardinal(d) === isCardinal(desired)));
			if (planB.length > 0) return planB;
		}

		// Plan C: return all
		return animations;
	}

	private filterLookAtHorizontalDirection(
		thing: LookAtHorizontalDirection,
		animations: AnimationConfig[],
		entry: TilesetConfigEntry,
	): AnimationConfig[] {
		const desired = thing.getLookingAtHorizontalDirection();

		// Plan A: animations that directly point to the desired direction
		const planA = animations.filter((a) => a.pointsTo.includes(desired));
		if (planA.length > 0) return planA;

		// Plan B: animations that point to E or W (can be flipped)
		if (entry.allowTransform) {
			const planB = animations.filter(
				(a) => a.pointsTo.includes("E" as Direction) || a.pointsTo.includes("W" as Direction),
			);
			if (planB.length > 0) return planB;
		}

		// Plan C: return all
		return animations;
	}

	private transformPointsToDirection(
		thing: PointsToDirection,
		animConfig: AnimationConfig,
		transformation: ImageTransformation,
	): void {
		const desired = thing.getDirection();
		if (desired === null) return;
		if (animConfig.pointsTo.includes(desired)) return;

		for (const animDir of animConfig.pointsTo) {
			if (isCardinal(animDir) !== isCardinal(desired)) continue;

			let current = animDir;
			while (current !== desired) {
				current = cycleDirection(current, 2);
				transformation.addRotation();
			}
			return;
		}
	}

	private transformLookAtHorizontalDirection(
		thing: LookAtHorizontalDirection,
		animConfig: AnimationConfig,
		transformation: ImageTransformation,
	): void {
		const desired = thing.getLookingAtHorizontalDirection();
		if (!animConfig.pointsTo.includes(desired) && animConfig.pointsTo.includes(reverseDirection(desired))) {
			transformation.addFlipH();
		}
	}

	private async loadAnimationFromConfig(
		animConfig: AnimationConfig,
		transformation: ImageTransformation,
	): Promise<SpriteAnimation> {
		const animation = new SpriteAnimation(animConfig.loop, []);

		for (const frameConfig of animConfig.frames) {
			const combinedWidth = frameConfig.width * animConfig.width;
			const combinedHeight = frameConfig.height * animConfig.height;
			const pixelWidth = Math.floor(this.cellWidth * combinedWidth);
			const pixelHeight = Math.floor(this.cellHeight * combinedHeight);
			const combinedOffsetX = frameConfig.offsetX + animConfig.offsetX;
			const combinedOffsetY = frameConfig.offsetY + animConfig.offsetY;

			const spriteTransform = new ImageTransformation();
			spriteTransform.addFlipH(transformation.flipH);
			spriteTransform.addFlipH(animConfig.horizontalFlip);
			spriteTransform.addFlipH(frameConfig.horizontalFlip);
			spriteTransform.addFlipV(transformation.flipV);
			spriteTransform.addFlipV(animConfig.verticalFlip);
			spriteTransform.addFlipV(frameConfig.verticalFlip);
			spriteTransform.addRotation(transformation.rotate + animConfig.rotation + frameConfig.rotation);

			const sprite = new Sprite();
			sprite.assetName = frameConfig.filename ?? "";
			sprite.width = combinedWidth;
			sprite.height = combinedHeight;
			sprite.rotation = spriteTransform.rotate;
			sprite.flipH = spriteTransform.flipH;
			sprite.flipV = spriteTransform.flipV;
			sprite.offsetX = combinedOffsetX;
			sprite.offsetY = combinedOffsetY;
			sprite.character = frameConfig.character;
			sprite.foreground = frameConfig.foreground;
			sprite.background = frameConfig.background;

			if (frameConfig.filename) {
				sprite.image = await getImage(frameConfig.filename, pixelWidth, pixelHeight, spriteTransform);
			}

			animation.addFrame(sprite, frameConfig.interval);
		}

		return animation;
	}
}

export function filterByState(state: unknown, animations: AnimationConfig[]): AnimationConfig[] {
	if (state == null) return animations;

	// Numeric state: match animations where any tag has a value equal to the state number.
	// Used by WallCrack: tags like { "stage": 0 }, state = 0.
	if (typeof state === "number") {
		const matched = animations.filter((anim) => Object.values(anim.tags).some((v) => v === state));
		return matched.length > 0 ? matched : animations;
	}

	// Boolean tag pattern: state is a string, match tag keys with value true.
	// Used by Fire, BigSquid, etc.: tags like { "hurt": true }, state = "hurt".
	const stateTag = String(state).toLowerCase();

	const matchingState: AnimationConfig[] = [];
	const defaultVariants: AnimationConfig[] = [];

	for (const anim of animations) {
		let hasMatchingTruthyTag = false;
		let hasAnyTruthyTag = false;

		for (const [key, value] of Object.entries(anim.tags)) {
			if (value === true) {
				hasAnyTruthyTag = true;
				if (key.toLowerCase() === stateTag) {
					hasMatchingTruthyTag = true;
				}
			}
		}

		if (hasMatchingTruthyTag) {
			matchingState.push(anim);
		} else if (!hasAnyTruthyTag) {
			defaultVariants.push(anim);
		}
	}

	if (matchingState.length > 0) return matchingState;
	if (defaultVariants.length > 0) return defaultVariants;
	return animations;
}
